import { useEffect, useState } from 'react';
import type {
  ExtensionBridge,
  ExtensionCapability,
} from '@doxyme/extensions-bridge';

/**
 * Utility hook to use a Capability as a React state component. Adds capability to the bridge on mount and remove it on unmount.
 * @param bridge - bridge instance
 * @param fn  - function that returns capability instance
 * @returns Capability instance.
 * @example
 * ```tsx
 * const modalApplet = useAddCapability(bridge, () => new ModalAppletCapability());
 * ```
 */
export function useAddCapability<T extends ExtensionCapability | undefined>(
  bridge: ExtensionBridge,
  fn: () => T,
): T {
  const [capability] = useState<T>(fn);

  useEffect(() => {
    if (!capability) {
      return () => void 0;
    }

    return addCapabilityToBridge(bridge, capability);
  }, []);

  return capability;
}

/**
 * Utility function that adds a capability to a bridge and returns a function that removes the capability
 * @param bridge - The extension bridge reference
 * @param capability - The capability to add to the bridge
 * @description This is useful for adding a capability to a bridge in a useEffect hook and removing it when the component unmounts
 * @example
 * ```ts
 * useEffect(() => {
 *  return addCapabilityToBridge(bridge, capability);
 * }, [bridge, capability]);
 * ```
 */
export function addCapabilityToBridge(
  bridge: ExtensionBridge,
  capability: ExtensionCapability,
): () => void {
  bridge.addCapability(capability);
  return () => bridge.removeCapability(capability.id);
}
